import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Collapse,
  Chip,
} from '@mui/material';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import { useKeyboard } from '../hooks/useKeyboard';
import NativeInput from '../components/NativeInput';
import logoImage from '../assets/FullLogo.png';
import config from '../config';
import { Capacitor } from '@capacitor/core';

// Carousel items for headings - adjusted for consistent line counts
const carouselItems = [
  {
    heading: 'Find Best Doctors & Book Your First Appointment',
    subheading: 'Login to discover top-rated specialists and schedule your consultation in just a few taps'
  },
  {
    heading: ' Your Health Journey Starts Here',
    subheading: 'Connect with expert doctors and book appointments instantly with just a few simple steps'
  },
  {
    heading: 'Access Quality Healthcare at Your Fingertips',
    subheading: 'Browse verified doctors, read reviews, and book your appointment in minutes'
  },
  {
    heading: 'Your Trusted Partner for Medical Appointments',
    subheading: 'Experience seamless booking with India\'s most trusted healthcare platform today'
  }
];

const BookmyopLogin = () => {
  const navigate = useNavigate();
  const { verifyOTP, otpState, sendOTP, resetOTPState } = useBookMyOpAuth();
  const { showKeyboard } = useKeyboard();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [debugOtp, setDebugOtp] = useState('');
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [apiConfig, setApiConfig] = useState(null);
  const [lastError, setLastError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  // Get API config on mount and update periodically
  useEffect(() => {
    const updateConfig = () => {
      try {
        const backend = config.backend;
        const isNative = Capacitor?.isNativePlatform?.() || false;
        const platform = Capacitor?.getPlatform() || 'web';
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'N/A';
        const protocol = typeof window !== 'undefined' ? window.location.protocol : 'N/A';
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent?.substring(0, 150) : 'N/A';
        const isWebView = /wv/i.test(userAgent);
        
        setApiConfig({
          backendHost: backend.host,
          apiBaseURL: `${backend.host}/api`,
          isNative,
          platform,
          hostname,
          protocol,
          userAgent,
          isWebView,
          isProduction: backend.host.includes('easyhealthdigital.com') && !backend.host.includes('dev.')
        });
      } catch (err) {
        console.error('Error getting API config:', err);
      }
    };
    
    // Update immediately
    updateConfig();
    
    // Update after delays to catch Capacitor initialization
    setTimeout(updateConfig, 100);
    setTimeout(updateConfig, 500);
    setTimeout(updateConfig, 1000);
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCarouselIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  // Enhanced extractor: show everything for both success and error
  const makeApiCallDetails = (result, isError = false) => {
    if (!result) return null;
    // For Axios error
    if (isError) {
      const err = result;
      const config = err?.originalError?.config || err?.config || {};
      const resp = err?.originalError?.response || err?.response || {};
      const reqHeaders = config.headers || {};
      const reqData = config.data;
      const respHeaders = resp.headers || {};
      const respData = resp.data;
      return {
        url: config.url,
        method: config.method,
        requestHeaders: reqHeaders,
        requestData: typeof reqData === 'string' ? reqData : JSON.stringify(reqData),
        status: resp.status,
        statusText: resp.statusText,
        responseHeaders: respHeaders,
        responseBody: typeof respData === 'string' ? respData : JSON.stringify(respData),
        code: err.code || err?.originalError?.code,
        message: err.message,
        stack: err.stack || err?.originalError?.stack,
        errorType: 'error',
        full: err,
      };
    } else {
      // For axios success response (e.g., sendOTP or verifyOTP success)
      const resp = result?.response || result;
      const cfg = resp?.config || {};
      const reqHeaders = cfg.headers || {};
      const reqData = cfg.data;
      const respHeaders = resp.headers || {};
      const respData = resp.data;
      return {
        url: cfg.url,
        method: cfg.method,
        requestHeaders: reqHeaders,
        requestData: typeof reqData === 'string' ? reqData : JSON.stringify(reqData),
        status: resp.status,
        statusText: resp.statusText,
        responseHeaders: respHeaders,
        responseBody: typeof respData === 'string' ? respData : JSON.stringify(respData),
        code: '',
        message: 'Success',
        stack: '',
        errorType: 'success',
        full: resp,
      };
    }
  };

  const [lastApiCall, setLastApiCall] = useState(null);

  // Helper to extract all details from an Axios error for debug panel
  const makeApiErrorDetails = (err) => {
    const config = err?.originalError?.config || err?.config || {};
    const resp = err?.originalError?.response || err?.response || {};
    const reqHeaders = config.headers || {};
    const reqData = config.data;
    // Axios attaches response headers as plain object
    const respHeaders = resp.headers || {};
    const respData = resp.data;
    return {
      // Human-readable quick overview
      url: config.url,
      method: config.method,
      requestHeaders: reqHeaders,
      requestData: typeof reqData === 'string' ? reqData : JSON.stringify(reqData),
      status: resp.status,
      statusText: resp.statusText,
      responseHeaders: respHeaders,
      responseBody: typeof respData === 'string' ? respData : JSON.stringify(respData),
      // Codes
      code: err.code || err?.originalError?.code,
      message: err.message,
      stack: err.stack || err?.originalError?.stack,
      // Full originals for deep debug
      originalError: err.originalError || undefined,
      response: resp,
      config,
    }
  }

  const handleSendOTP = async () => {
    setError("");
    setLastError(null);
    setLastApiCall(null);
    if (!phoneNumber || phoneNumber.length < 10) {
      setError("Please enter a valid phone number");
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      const response = await sendOTP(phoneNumber);
      setLastApiCall(makeApiCallDetails(response));
      if (response?.debug_otp) {
        setDebugOtp(response.debug_otp);
      }
    } catch (err) {
      setError(err.message || "Failed to send OTP. Please try again.");
      setLastApiCall(makeApiCallDetails(err, true));
    } finally {
      setLoading(false);
    }
  };

  // PATCH: fix OTP payload bug, only call verifyOTP(otp) as context manages phone
  const handleVerifyOTP = async () => {
    setLastError(null);
    setLastApiCall(null);
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }
    if (otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const response = await verifyOTP(otp);  // <--- only otp!
      setLastApiCall(makeApiCallDetails(response));
      if (response.user) {
        navigate("/");
      }
    } catch (err) {
      setError(err.message || "Invalid OTP. Please try again.");
      setLastApiCall(makeApiCallDetails(err, true));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setPhoneNumber('');
    setOtp('');
    setDebugOtp('');
    setError('');
    resetOTPState(); // Reset the OTP state in the context
  };

  const onFocus = (e) => {
    // Small timeout so keyboard begins showing
    setTimeout(() => {
      const input = e.target;
      if (containerRef.current && input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  return (
    <Box
      ref={containerRef}
      className="app-scroll-container"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3 },
        position: 'relative',
      }}
    >
      <Container maxWidth="md" sx={{ width: '100%' }}>
      <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 2, sm: 3, md: 0 },
              width: { xs: '100%', md: 'auto' },
              height: { xs: '180px', sm: '200px', md: '200px' },
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {carouselItems.map((item, index) => {
                const isActive = index === currentCarouselIndex;
                const isPrev = index === (currentCarouselIndex - 1 + carouselItems.length) % carouselItems.length;
                const isNext = index === (currentCarouselIndex + 1) % carouselItems.length;
                
                return (
                  <Box
                    key={index}
                    sx={{
                      position: isActive ? 'relative' : 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      opacity: isActive ? 1 : 0,
                      transform: isActive 
                        ? 'translateY(0) rotateX(0deg)' 
                        : isPrev 
                        ? 'translateY(-100%) rotateX(90deg)' 
                        : 'translateY(100%) rotateX(-90deg)',
                      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      transformStyle: 'preserve-3d',
                      backfaceVisibility: 'hidden',
                      perspective: '1000px',
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                        mb: 1,
                        letterSpacing: '0.5px',
                        transform: 'translateZ(0)',
                        height: { xs: '3.5rem', sm: '4.5rem', md: '5.5rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                        textAlign: 'center',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.heading}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.75rem', sm: '1rem', md: '1.1rem' },
                        fontWeight: 400,
                        opacity: 0.9,
                        maxWidth: { xs: '100%', md: '500px' },
                        mx: 'auto',
                        transform: 'translateZ(0)',
                        height: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                        textAlign: 'center',
                      }}
                    >
                      {item.subheading}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            
            {/* Carousel Indicators */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                mt: 2,
              }}
            >
              {carouselItems.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentCarouselIndex(index)}
                  sx={{
                    width: currentCarouselIndex === index ? 24 : 8,
                    height: 8,
                    borderRadius: '4px',
                    backgroundColor: currentCarouselIndex === index 
                      ? theme.palette.primary.main 
                      : theme.palette.grey[400],
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, sm: 3, md: 4 },
            position: 'relative',
            zIndex: 1,
            width: '100%',
          }}
        >
          {/* Animated Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' },
              mb: { xs: 0, md: 0 },
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="BookMyOP Logo"
              sx={{
                width: { xs: '200px', sm: '280px', md: '450px' },
                height: { xs: '200px', sm: '280px', md: '450px' },
                maxWidth: { xs: '200px', sm: '280px', md: '500px' },
                maxHeight: { xs: '200px', sm: '280px', md: '500px' },
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Heading and Subheading Carousel */}
         

          {/* Title outside card for mobile */}
          {/* {isMobile && (
            <Typography
              component="h1"
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                opacity: 0.8,
                width: '100%',
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                mb: 3,
                background: 'linear-gradient(45deg, #03045e, #03045e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: 'Sansation, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                fontSize: '2.2rem',
                
              }}
            >
              Book My OP
            </Typography>
          )} */}

          <Paper
            elevation={8}
            sx={{
              p: { xs: 3, sm: 4, md: 4 },
              width: '100%',
              maxWidth: { xs: '100%', sm: '500px', md: '450px' },
              borderRadius: 3,
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(145deg, #1e1e1e, #2a2a2a)'
                : 'linear-gradient(145deg, #ffffff, #f5f8ff)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1), 0 4px 10px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 35px rgba(0,0,0,0.15), 0 6px 15px rgba(0,0,0,0.1)',
              },
            }}
          >
            {/* Title inside card for desktop */}
            {/* {!isMobile && (
              <Typography
                component="h1"
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  color: theme.palette.primary.main,
                  letterSpacing: '-0.5px',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.1)',
                  mb: 3,
                  fontSize: { xs: '1.5rem', sm: '1.5rem' },
                  fontWeight: 900,
                  fontFamily: 'Sansation, sans-serif',
                }}
              >
                BOOK MY OP
              </Typography>
            )} */}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 235, 238, 0.9)',
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" noValidate>
              <Box sx={{ mb: 2 }}>
                {/* <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontSize: { xs: '1.2rem', sm: '1.2rem' }, 
                    color: theme.palette.text.secondary,
                    fontWeight: 900 
                  }}
                >
                  Phone Number
                </Typography> */}
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <Typography 
                    sx={{ 
                      position: 'absolute', 
                      left: 16, 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: theme.palette.text.primary, 
                      fontWeight: 900, 
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      zIndex: 1,
                      pointerEvents: 'none'
                    }}
                  >
                    +91
                  </Typography>
                  <NativeInput
                    type="tel"
                    // placeholder="Enter 10-digit mobile number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                      if (value.length <= 10) {
                        setPhoneNumber(value);
                      }
                    }}
                    onFocus={onFocus}
                    maxLength={10}
                    disabled={loading || otpState.isOtpSent}
                    id="phone"
                    data-testid="phone-input"
                    style={{
                      paddingLeft: '60px',
                      fontSize: '10px !important', // Prevents zoom on iOS
                      height: '56px',
                      width: '100%',
                      border: `2px solid ${theme.palette.grey[300]}`,
                      
                    }}
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mt: 0.5, 
                    display: 'block',
                    color: theme.palette.text.secondary 
                  }}
                >
                  {otpState.attemptsRemaining < 5 ? `${otpState.attemptsRemaining} attempts remaining` : 'Enter 10-digit mobile number'}
                </Typography>
              </Box>
              
              {otpState.isOtpSent && (
                <>
                  <Box sx={{ mb: debugOtp ? 1 : 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 1, 
                        fontSize: { xs: '1.3rem', sm: '1.3rem' }, 
                        color: theme.palette.text.secondary,
                        fontWeight: 900 
                      }}
                    >
                      Enter OTP
                    </Typography>
                    <NativeInput
                      type="tel"
                      placeholder="Enter 4-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      onFocus={onFocus}
                      maxLength={4}
                      disabled={loading}
                      id="otp"
                      data-testid="otp-input"
                      style={{
                        fontSize: '16px', // Prevents zoom on iOS
                        height: '56px',
                        width: '100%',
                        border: `2px solid ${theme.palette.grey[300]}`,
                      }}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                  </Box>
                  {debugOtp && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        border: '1px solid #bbdefb',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          fontWeight: 600,
                          color: '#1976d2',
                          mb: 0.5,
                        }}
                      >
                        Development OTP
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          fontWeight: 700,
                          color: '#0d47a1',
                          letterSpacing: '2px',
                          fontFamily: 'monospace',
                        }}
                      >
                        {debugOtp}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={otpState.isOtpSent ? handleVerifyOTP : handleSendOTP}
                disabled={loading || otpState.attemptsRemaining === 0}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                
                  '&:disabled': {
                    background: theme.palette.grey[400],
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : otpState.isOtpSent ? 'Verify OTP' : 'Send OTP'}
              </Button>

              {otpState.isOtpSent && (
                <>
                  <Button
                    fullWidth
                    color="secondary"
                    onClick={handleSendOTP}
                    disabled={loading || otpState.attemptsRemaining === 0}
                    sx={{
                      mt: 1,
                      py: 1,
                      borderRadius: '12px',
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      fontWeight: 500,
                      textTransform: 'none',
                      color: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.secondary.dark,
                      },
                    }}
                  >
                    Resend OTP
                  </Button>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleChangeNumber}
                    disabled={loading}
                    sx={{
                      mt: 1,
                      py: 1,
                      borderRadius: '12px',
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      fontWeight: 500,
                      textTransform: 'none',
                      borderColor: theme.palette.grey[400],
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        backgroundColor: theme.palette.grey[50],
                      },
                    }}
                  >
                    Change Number
                  </Button>
                </>
              )}
            </Box>

            <Divider sx={{ my: 3, '&::before, &::after': { borderColor: theme.palette.grey[300] } }}>
             
            </Divider>

          </Paper>

          {/* --- DEBUG NETWORK/API PANEL: Show for all (success or error) --- */}
          {lastApiCall && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Paper elevation={1} sx={{
                p: 2, borderRadius: 2, fontSize: '0.85rem', bgcolor: lastApiCall.errorType === 'error' ? '#fff3e0' : '#e8f5e9', color: 'text.primary', boxShadow: '0 2px 12px #bdbdbd22', mb:2}}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: lastApiCall.errorType === 'error' ? 'error.main' : 'success.main', mb: .5 }}>
                  API {lastApiCall.errorType === 'error' ? 'Error' : 'Call'} Details
                </Typography>
                {lastApiCall.url && <Box><strong>URL:</strong> {lastApiCall.url}</Box>}
                {lastApiCall.method && <Box><strong>Method:</strong> {lastApiCall.method}</Box>}
                {typeof lastApiCall.status !== 'undefined' && <Box><strong>Status:</strong> {String(lastApiCall.status)}</Box>}
                {lastApiCall.statusText && <Box><strong>Status Text:</strong> {lastApiCall.statusText}</Box>}
                {lastApiCall.code && <Box><strong>Code:</strong> {String(lastApiCall.code)}</Box>}
                {lastApiCall.message && <Box><strong>Message:</strong> {lastApiCall.message}</Box>}

                {lastApiCall.requestHeaders && (
                  <details style={{marginTop:8}}>
                    <summary><strong>Request Headers</strong></summary>
                    <pre style={{fontSize:'11px'}}>{JSON.stringify(lastApiCall.requestHeaders, null, 2)}</pre>
                  </details>
                )}
                {lastApiCall.requestData && lastApiCall.requestData !== '{}' && (
                  <details style={{marginTop:8}}>
                    <summary><strong>Request Data</strong></summary>
                    <pre style={{fontSize:'11px'}}>{lastApiCall.requestData}</pre>
                  </details>
                )}
                {lastApiCall.responseHeaders && Object.keys(lastApiCall.responseHeaders).length > 0 && (
                  <details style={{marginTop:8}}>
                    <summary><strong>Response Headers</strong></summary>
                    <pre style={{fontSize:'11px'}}>{JSON.stringify(lastApiCall.responseHeaders, null, 2)}</pre>
                  </details>
                )}
                {lastApiCall.responseBody && lastApiCall.responseBody !== '{}' && (
                  <details style={{marginTop:8}}>
                    <summary><strong>Response Body</strong></summary>
                    <pre style={{fontSize:'11px'}}>{lastApiCall.responseBody}</pre>
                  </details>
                )}
                {/* Show Full Error/Response for deep troubleshooting */}
                <details style={{marginTop:8}}>
                  <summary><strong>Raw {lastApiCall.errorType === 'error' ? 'Error' : 'Response'} Object</strong></summary>
                  <pre style={{fontSize:'10px', color:'#ad1457', whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{JSON.stringify(lastApiCall.full, null, 2)}</pre>
                </details>
              </Paper>
            </Box>
          )}

          {/* Debug Panel Toggle */}
          {/* <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small"
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              sx={{
                fontSize: '0.7rem',
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              {showDebugPanel ? 'Hide Debug Info' : 'Show Debug Info'}
            </Button>
            {apiConfig && (
              <Button
                size="small"
                onClick={async () => {
                  try {
                    setError('');
                    const testUrl = `${apiConfig.apiBaseURL}/bookmyop/login/`;
                    const origin = window.location.origin;
                    const protocol = window.location.protocol;
                    const hostname = window.location.hostname;
                    
                    console.log('🧪 Testing API connection to:', testUrl);
                    console.log('🧪 Origin:', origin);
                    console.log('🧪 Protocol:', protocol);
                    console.log('🧪 Hostname:', hostname);
                    console.log('🧪 Full URL:', window.location.href);
                    
                    // Store test details
                    const testDetails = {
                      url: testUrl,
                      origin: origin,
                      protocol: protocol,
                      hostname: hostname,
                      timestamp: new Date().toLocaleTimeString(),
                      status: 'testing'
                    };
                    setLastError(testDetails);
                    
                    const response = await fetch(testUrl, {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    console.log('🧪 API Test Response:', {
                      status: response.status,
                      statusText: response.statusText,
                      ok: response.ok,
                      headers: Object.fromEntries(response.headers.entries())
                    });
                    
                    const data = await response.json();
                    
                    // Success
                    const successMsg = `✅ API Test Success!\n\nStatus: ${response.status} ${response.statusText}\nURL: ${testUrl}\n\nResponse: ${JSON.stringify(data, null, 2)}`;
                    alert(successMsg);
                    
                    setLastError({
                      ...testDetails,
                      status: 'success',
                      statusCode: response.status,
                      statusText: response.statusText,
                      response: data
                    });
                    
                  } catch (err) {
                    console.error('🧪 API Test Error:', err);
                    
                    // Detailed error information
                    const origin = window.location.origin;
                    const protocol = window.location.protocol;
                    const hostname = window.location.hostname;
                    
                    const errorDetails = {
                      message: err.message,
                      name: err.name,
                      stack: err.stack,
                      url: `${apiConfig.apiBaseURL}/bookmyop/login/`,
                      origin: origin,
                      protocol: protocol,
                      hostname: hostname,
                      timestamp: new Date().toLocaleTimeString(),
                      code: err.code,
                      type: err.type
                    };
                    
                    setLastError({
                      ...errorDetails,
                      status: 'failed'
                    });
                    
                    // More detailed error message
                    let errorMsg = `❌ API Test Failed\n\n`;
                    errorMsg += `Error: ${err.message}\n`;
                    errorMsg += `Type: ${err.name || 'Unknown'}\n`;
                    errorMsg += `URL: ${apiConfig.apiBaseURL}/bookmyop/login/\n`;
                    errorMsg += `Origin: ${origin}\n`;
                    errorMsg += `Protocol: ${protocol}\n\n`;
                    
                    if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                      errorMsg += `⚠️ This is likely a CORS issue!\n\n`;
                      errorMsg += `Your Django backend needs to allow:\n`;
                      errorMsg += `• Origin: ${origin}\n`;
                      if (origin.includes('capacitor')) {
                        errorMsg += `• Also add: capacitor://localhost\n`;
                        errorMsg += `• Also add: capacitor-https://localhost\n`;
                      }
                      errorMsg += `\nAdd to Django settings.py:\n`;
                      errorMsg += `CORS_ALLOWED_ORIGIN_REGEXES = [\n`;
                      errorMsg += `    r"^capacitor://.*",\n`;
                      errorMsg += `    r"^capacitor-https://.*",\n`;
                      errorMsg += `]\n`;
                      errorMsg += `CORS_ALLOW_CREDENTIALS = True\n`;
                    }
                    
                    errorMsg += `\nCheck debug panel for more details.`;
                    
                    alert(errorMsg);
                    setError(`API Test Failed: ${err.message}`);
                    
                    console.error('🧪 Full error details:', {
                      error: err,
                      origin,
                      protocol,
                      hostname,
                      url: `${apiConfig.apiBaseURL}/bookmyop/login/`
                    });
                  }
                }}
                sx={{
                  fontSize: '0.7rem',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: '#fff',
                  }
                }}
              >
                Test API Connection
              </Button>
            )}
          </Box> */}

          {/* Debug Panel */}
          {/* <Collapse in={showDebugPanel}>
            <Paper
              elevation={2}
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                🔧 API Configuration
              </Typography>
              {apiConfig ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <strong>Backend Host:</strong> <span style={{ color: apiConfig.isProduction ? 'green' : apiConfig.backendHost.includes('dev.') ? 'orange' : 'red', fontWeight: 'bold' }}>{apiConfig.backendHost}</span>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>API Base URL:</strong> {apiConfig.apiBaseURL}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Platform:</strong> {apiConfig.platform} 
                    {apiConfig.isNative && <Chip label="Native" size="small" sx={{ ml: 1, height: 18 }} />}
                    {apiConfig.isWebView && <Chip label="WebView" size="small" sx={{ ml: 1, height: 18, bgcolor: 'info.main' }} />}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Environment:</strong> {apiConfig.isProduction ? 'Production' : 'Local'} 
                    {apiConfig.isProduction && <Chip label="✓" size="small" sx={{ ml: 1, height: 18, bgcolor: 'success.main', color: 'white' }} />}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Protocol:</strong> {apiConfig.protocol}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <strong>Hostname:</strong> {apiConfig.hostname}
                  </Box>
                  <Box sx={{ mb: 1, wordBreak: 'break-all' }}>
                    <strong>Origin:</strong> <span style={{ color: apiConfig.protocol?.includes('capacitor') ? 'red' : 'inherit', fontWeight: 'bold' }}>{typeof window !== 'undefined' ? window.location.origin : 'N/A'}</span>
                  </Box>
                  <Box sx={{ mb: 1, wordBreak: 'break-all', fontSize: '0.7rem' }}>
                    <strong>User Agent:</strong> {apiConfig.userAgent}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading config...
                </Typography>
              )}

              {lastError && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700, color: lastError.status === 'success' ? 'success.main' : 'error.main' }}>
                    {lastError.status === 'success' ? '✅' : lastError.status === 'testing' ? '🔄' : '❌'} Last Test ({lastError.timestamp})
                  </Typography>
                  {lastError.status && (
                    <Box sx={{ mb: 1 }}>
                      <strong>Status:</strong> 
                      <Chip 
                        label={lastError.status} 
                        size="small" 
                        sx={{ 
                          ml: 1, 
                          height: 18,
                          bgcolor: lastError.status === 'success' ? 'success.main' : lastError.status === 'testing' ? 'info.main' : 'error.main',
                          color: 'white'
                        }} 
                      />
                    </Box>
                  )}
                  {lastError.message && (
                    <Box sx={{ mb: 1 }}>
                      <strong>Message:</strong> {lastError.message}
                    </Box>
                  )}
                  {lastError.statusCode && (
                    <Box sx={{ mb: 1 }}>
                      <strong>HTTP Status:</strong> {lastError.statusCode} {lastError.statusText}
                    </Box>
                  )}
                  {lastError.url && (
                    <Box sx={{ mb: 1, wordBreak: 'break-all' }}>
                      <strong>URL:</strong> {lastError.url}
                    </Box>
                  )}
                  {lastError.origin && (
                    <Box sx={{ mb: 1, wordBreak: 'break-all' }}>
                      <strong>Origin:</strong> <span style={{ color: 'red', fontWeight: 'bold' }}>{lastError.origin}</span>
                      {lastError.origin.includes('capacitor') && (
                        <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fff3cd', borderRadius: 1, fontSize: '0.7rem' }}>
                          ⚠️ <strong>CORS Issue:</strong> Django needs to allow this origin in CORS_ALLOWED_ORIGIN_REGEXES
                        </Box>
                      )}
                    </Box>
                  )}
                  {lastError.baseURL && (
                    <Box sx={{ mb: 1, wordBreak: 'break-all' }}>
                      <strong>Base URL:</strong> {lastError.baseURL}
                    </Box>
                  )}
                  {lastError.code && (
                    <Box sx={{ mb: 1 }}>
                      <strong>Error Code:</strong> {lastError.code}
                    </Box>
                  )}
                  {lastError.name && (
                    <Box sx={{ mb: 1 }}>
                      <strong>Error Type:</strong> {lastError.name}
                    </Box>
                  )}
                  {lastError.type && (
                    <Box sx={{ mb: 1 }}>
                      <strong>Network Type:</strong> {lastError.type}
                    </Box>
                  )}
                  {lastError.response && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1, fontSize: '0.7rem' }}>
                      <strong>Response:</strong> {JSON.stringify(lastError.response, null, 2)}
                    </Box>
                  )}
                </>
              )}

              <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
                💡 To view logs: adb logcat | grep -E "OTP|CSRF|API|🎯"
              </Typography>
            </Paper>
          </Collapse> */}

        </Box>

        {/* Company Name at Bottom */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            mt: { xs: 3, sm: 4, md: 4 },
            mb: { xs: 2, sm: 0 },
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
            fontWeight: 500,
            letterSpacing: '0.5px',
            opacity: 0.8,
            width: '100%',
            position: 'relative',
          }}
        >
          EASY HEALTH DIGITAL SOLUTIONS
        </Typography>
      </Container>
    </Box>
  );
};

export default BookmyopLogin;